import React from 'react';
import { HardHat, Rocket, Camera, ArrowsLeftRight, CopySimple, MapPin, CreditCard, DeviceMobile, ArrowRight, CheckCircle, Star, Heart, Users } from '@phosphor-icons/react';
import TopArtisans from './TopArtisans';

const FEATURES = [
  { icon: Camera, title: "Photo + IA", desc: "Uploadez une photo de chantier, l'IA génère le post parfait" },
  { icon: ArrowsLeftRight, title: "Avant / Après", desc: "Montrez la transformation avec deux photos comparatives" },
  { icon: CopySimple, title: "Copier-Coller", desc: "Un clic pour copier sur Facebook ou WhatsApp" },
  { icon: Users, title: "Galerie publique", desc: "Partagez vos meilleurs projets et inspirez la communauté" },
];

const PRICING = [
  { name: "Essai", price: "0", period: "", features: ["2 posts d'essai", "Tous les templates", "Copie presse-papiers", "Upload 1 photo"], cta: "Essayer gratuitement", highlight: false },
  { name: "Pro", price: "17,90", period: "/mois", features: ["Posts illimités", "Mode Avant/Après", "Multi-images (5 max)", "Galerie publique", "Tous les templates", "Support prioritaire"], cta: "Passer en Pro", highlight: true },
];

export default function LandingPage({ onGetStarted, onGallery, onHowItWorks, onBrief }) {
  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="landing-page">
      {/* Nav */}
      <nav className="bg-[#09090B] border-b border-[#27272A] sticky top-0 z-50 backdrop-blur-sm bg-opacity-90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
              <HardHat size={18} weight="duotone" className="text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Shanga Pro</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onBrief} className="text-sm text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors hidden sm:block" data-testid="nav-brief-btn">Présentation</button>
            <button onClick={onHowItWorks} className="text-sm text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors hidden sm:block" data-testid="nav-how-btn">Comment ça marche</button>
            <button onClick={onGallery} className="text-sm text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors hidden sm:block" data-testid="nav-gallery-btn">Galerie</button>
            <a href="#pricing" className="text-sm text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors hidden sm:block">Tarifs</a>
            <button onClick={onGetStarted} data-testid="nav-login-btn"
              className="h-9 px-4 text-sm bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] font-medium transition-colors">
              Connexion
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-full mb-6">
              <Rocket size={14} weight="duotone" className="text-[#A78BFA]" />
              <span className="text-xs font-bold tracking-wide text-[#A78BFA]">N°1 du marketing BTP à Mayotte</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-5 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Transformez vos
              <br />
              <span className="text-[#8B5CF6]">chantiers</span> en
              <br />
              clients.
            </h1>
            <p className="text-lg text-[#A1A1AA] leading-relaxed mb-8 max-w-lg">
              Une photo, une description — l'IA génère le post Facebook ou WhatsApp parfait pour attirer des appels immédiats.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={onGetStarted} data-testid="hero-cta-btn"
                className="h-14 px-8 bg-[#8B5CF6] text-white font-bold text-base rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center justify-center gap-2 animate-glow">
                Essayer gratuitement <ArrowRight size={20} weight="bold" />
              </button>
              <button onClick={onGallery}
                className="h-14 px-8 border border-[#27272A] bg-[#18181B] text-[#F4F4F5] font-medium text-base rounded-lg hover:bg-[#1F1F23] hover:border-[#3F3F46] transition-colors flex items-center justify-center gap-2">
                <Heart size={18} weight="duotone" /> Voir la galerie
              </button>
            </div>
            <p className="text-xs text-[#71717A] mt-4">2 posts d'essai gratuits. Sans carte bancaire.</p>
          </div>
          <div className="animate-fade-in-up stagger-2 hidden lg:block">
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1759389964108-1940ff61db0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjcmFmdHNtYW4lMjBidWlsZGluZyUyMHRyb3BpY2FsJTIwaXNsYW5kJTIwaG9tZXxlbnwwfHx8fDE3NzYxMzA5MjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Villa tropicale" className="rounded-lg border border-[#27272A] w-full h-[420px] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-[#18181B] border border-[#27272A] rounded-lg p-4 max-w-[280px] shadow-2xl shadow-[#8B5CF6]/10 animate-slide-in-right stagger-3">
                <p className="text-sm leading-relaxed text-[#E4E4E7]">
                  🛠️ Nouveau chantier terminé à Bandrélé !<br />
                  Toiture renforcée pour protéger cette maison. 🌧️<br />
                  📞 Contactez-nous pour votre devis gratuit.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-7 px-3 bg-[#8B5CF6] text-white text-xs rounded-md flex items-center gap-1">
                    <CopySimple size={12} /> Copier
                  </div>
                  <span className="text-[10px] text-[#71717A]">Généré par IA</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-[#27272A] py-16 sm:py-20 bg-[#0F0F14]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Tout pour booster votre visibilité
            </h2>
            <p className="text-[#A1A1AA] text-lg">Des outils conçus pour les artisans du BTP à Mayotte</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <div key={f.title} className={`border border-[#27272A] rounded-lg p-5 hover:border-[#8B5CF6]/40 bg-[#18181B] transition-all animate-fade-in-up stagger-${i + 1}`}>
                <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center mb-3">
                  <f.icon size={22} weight="duotone" className="text-[#A78BFA]" />
                </div>
                <h3 className="font-bold text-base mb-1 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{f.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center mb-12 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Simple comme 1, 2, 3
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: "1", title: "Décrivez", desc: "Choisissez la ville, le type de travaux et décrivez votre chantier." },
              { step: "2", title: "Générez", desc: "L'IA analyse votre chantier et crée un post marketing optimisé." },
              { step: "3", title: "Publiez", desc: "Copiez le post en un clic et collez-le sur Facebook ou WhatsApp." },
            ].map((s, i) => (
              <div key={s.step} className={`text-center animate-fade-in-up stagger-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-[#8B5CF6] text-white font-black text-xl flex items-center justify-center mx-auto mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {s.step}
                </div>
                <h3 className="font-bold text-lg mb-2 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Artisans */}
      <TopArtisans />

      {/* Pricing */}
      <section id="pricing" className="border-y border-[#27272A] py-16 sm:py-20 bg-[#0F0F14]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Tarifs simples</h2>
            <p className="text-[#A1A1AA] text-lg">Essayez gratuitement, passez en Pro quand vous voulez</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {PRICING.map((p) => (
              <div key={p.name} data-testid={`pricing-${p.name.toLowerCase()}`}
                className={`border rounded-lg p-6 ${p.highlight ? 'border-[#8B5CF6] bg-[#8B5CF6]/5 ring-1 ring-[#8B5CF6]' : 'border-[#27272A] bg-[#18181B]'}`}>
                {p.highlight && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <Star size={14} weight="fill" className="text-[#A78BFA]" />
                    <span className="text-xs font-bold text-[#A78BFA] uppercase tracking-wider">Populaire</span>
                  </div>
                )}
                <h3 className="font-bold text-xl mb-1 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{p.name}</h3>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-4xl font-black tracking-tight text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{p.price}€</span>
                  <span className="text-sm text-[#71717A]">{p.period}</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#E4E4E7]">
                      <CheckCircle size={16} weight="duotone" className="text-[#8B5CF6] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={onGetStarted}
                  className={`w-full h-12 rounded-lg font-bold text-sm transition-colors ${p.highlight ? 'bg-[#8B5CF6] text-white hover:bg-[#7C3AED]' : 'bg-[#27272A] text-[#F4F4F5] hover:bg-[#3F3F46]'}`}>
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <DeviceMobile size={40} weight="duotone" className="mx-auto text-[#A78BFA] mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Installez l'app sur votre téléphone
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-6 max-w-lg mx-auto">
            Ajoutez BTP Mayotte à votre écran d'accueil pour un accès rapide depuis le chantier.
          </p>
          <button onClick={onGetStarted} data-testid="final-cta-btn"
            className="h-14 px-8 bg-[#8B5CF6] text-white font-bold text-base rounded-lg hover:bg-[#7C3AED] transition-colors inline-flex items-center gap-2">
            Commencer maintenant <ArrowRight size={20} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[#8B5CF6] flex items-center justify-center">
              <HardHat size={14} weight="duotone" className="text-white" />
            </div>
            <span className="text-sm font-bold text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Shanga Pro</span>
          </div>
          <p className="text-xs text-[#71717A]">© 2026 Shanga Pro. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
